// src/utils/fatturazioneCommessa.js
// ═══════════════════════════════════════════════════════════════════════════
// RICHIESTE DI FATTURAZIONE PER PM — avanzamento della commessa (ottobre 2026)
// ═══════════════════════════════════════════════════════════════════════════
// Ogni PM vede solo le richieste di fatturazione fatte da lui; titolare, admin e chi ha il
// permesso «da-fatturare» (di default Gestione aziendale e Amministrazione) le vedono tutte
// (firestore rules 57). Alcuni numeri però riguardano la COMMESSA, non chi ha fatto la
// richiesta: quanto del contratto è già stato chiesto in fattura (per non superare il 100%),
// quanto aspetta l'amministrazione, quanto è stato fatturato. Questi totali si salvano sul
// budget della commessa nel campo «fatturazioneCalcolata» (stesso schema di
// variantiCalcolate): li leggono tutti, senza vedere le richieste degli altri.
//
//  • chi vede tutte le richieste ricalcola i totali da zero (sincronizzaFatturazioneCommessa);
//  • il PM che crea o annulla una sua richiesta li aggiorna nella stessa transazione;
//  • il fatturato e le richieste in attesa si riportano anche sul Computo SAL della commessa
//    (fatturato / richiestoInAttesa), così il SAL e la Sezione Commerciale di Insight
//    non contano due volte lo stesso lavoro. Se la commessa non ha richieste di
//    fatturazione, il fatturato del SAL resta quello scritto a mano, come prima.
// ═══════════════════════════════════════════════════════════════════════════
import { collection, query, where, getDocs, doc, getDoc, setDoc, serverTimestamp, runTransaction } from "firebase/firestore";
import { db } from "../firebase";
import { numIt } from "./economiaCommessa";

export const STATI_IN_ATTESA = ["da_fatturare", "in_lavorazione"];
const tondo = n => Math.round((Number(n) || 0) * 100) / 100;

/** Totali di una commessa dalle sue richieste (le annullate non contano). */
export function riepilogoRichieste(richieste = []) {
  const r = { percRichiesta: 0, importoInAttesa: 0, nInAttesa: 0, nDaFatturare: 0, importoFatturato: 0, nFatturate: 0, nRichieste: 0 };
  richieste.forEach(x => {
    if (!x || x.stato === "annullata") return;
    r.nRichieste++;
    r.percRichiesta += parseFloat(x.percentuale) || 0;
    const imp = numIt(x.importoRichiesto);
    if (STATI_IN_ATTESA.includes(x.stato)) { r.importoInAttesa += imp; r.nInAttesa++; }
    if (x.stato === "da_fatturare") r.nDaFatturare++;
    if (x.stato === "fatturata") { r.importoFatturato += imp; r.nFatturate++; }
  });
  r.percRichiesta = tondo(r.percRichiesta);
  r.importoInAttesa = tondo(r.importoInAttesa);
  r.importoFatturato = tondo(r.importoFatturato);
  return r;
}

const CAMPI = ["percRichiesta", "importoInAttesa", "nInAttesa", "nDaFatturare", "importoFatturato", "nFatturate", "nRichieste"];
export const stessiTotali = (a, b) => !!a && !!b && !!a.parziale === !!b.parziale && CAMPI.every(k => Math.abs((a[k] || 0) - (b[k] || 0)) < 0.005);

/** Somma di una richiesta ai totali (segno +1 quando si crea, -1 quando si annulla). */
export function applicaRichiesta(tot, richiesta, segno) {
  const t = { ...riepilogoRichieste([]), ...(tot || {}) };
  const imp = numIt(richiesta.importoRichiesto);
  t.nRichieste += segno;
  t.percRichiesta = tondo(t.percRichiesta + segno * (parseFloat(richiesta.percentuale) || 0));
  if (STATI_IN_ATTESA.includes(richiesta.stato)) { t.importoInAttesa = tondo(t.importoInAttesa + segno * imp); t.nInAttesa += segno; }
  if (richiesta.stato === "da_fatturare") t.nDaFatturare += segno;
  CAMPI.forEach(k => { if (t[k] < 0.005) t[k] = 0; });
  return t;
}

const idBudget = projectId => "budget_" + projectId;

async function budgetDellaCommessa(projectId, orgId) {
  const snap = await getDocs(query(collection(db, "budgetCommessa"), where("projectId", "==", projectId), where("organizationId", "==", orgId)));
  return snap.empty ? null : snap.docs[0];
}

/** Riporta fatturato e richieste in attesa sul Computo SAL della commessa (se esiste). */
async function riportaSulSAL(projectId, orgId, tot, conFatturato) {
  const snap = await getDocs(query(collection(db, "computiSAL"), where("projectId", "==", projectId), where("orgId", "==", orgId)));
  await Promise.all(snap.docs.map(d => {
    const s = d.data();
    const dati = { richiestoInAttesa: tot.importoInAttesa };
    if (conFatturato) {
      // Con almeno una richiesta il fatturato del SAL è quello delle richieste fatturate;
      // senza richieste torna modificabile a mano (dal valore attuale).
      dati.fatturatoDaRichieste = tot.nRichieste > 0;
      if (tot.nRichieste > 0) dati.fatturato = tot.importoFatturato;
    }
    const uguale = Object.keys(dati).every(k => typeof dati[k] === "number" ? Math.abs((s[k] || 0) - dati[k]) < 0.005 : s[k] === dati[k]);
    if (uguale) return null;
    return setDoc(doc(db, "computiSAL", d.id), { ...dati, updatedAt: serverTimestamp() }, { merge: true });
  }));
}

/**
 * Ricalcolo completo dei totali di una commessa. Solo per chi vede tutte le richieste
 * (le regole rifiutano agli altri la lettura completa). Scrive solo se qualcosa cambia.
 */
export async function sincronizzaFatturazioneCommessa(projectId, orgId) {
  if (!projectId || !orgId) return null;
  const [ricSnap, bud] = await Promise.all([
    getDocs(query(collection(db, "richiesteFatturazione"), where("projectId", "==", projectId), where("organizationId", "==", orgId))),
    budgetDellaCommessa(projectId, orgId),
  ]);
  const tot = { ...riepilogoRichieste(ricSnap.docs.map(d => d.data())), parziale: false };
  const salvati = bud?.data()?.fatturazioneCalcolata;
  if (!stessiTotali(salvati, tot) && (bud || tot.nRichieste > 0)) {
    await setDoc(doc(db, "budgetCommessa", bud ? bud.id : idBudget(projectId)),
      bud ? { fatturazioneCalcolata: { ...tot, aggiornatiIl: serverTimestamp() } }
          : { projectId, organizationId: orgId, fatturazioneCalcolata: { ...tot, aggiornatiIl: serverTimestamp() } },
      { merge: true });
  }
  await riportaSulSAL(projectId, orgId, tot, true);
  return tot;
}

/** Ricalcolo di tutte le commesse che hanno richieste (pagina delle richieste, chi vede tutto). */
export async function sincronizzaTutteLeCommesse(richieste, orgId) {
  const ids = [...new Set(richieste.map(r => r.projectId).filter(Boolean))];
  for (const id of ids) {
    try { await sincronizzaFatturazioneCommessa(id, orgId); }
    catch (e) { console.warn("Totali fatturazione non aggiornati per la commessa", id, e); }
  }
}

/**
 * Crea una richiesta e aggiorna i totali della commessa nella stessa transazione.
 * Il limite del 100% si controlla sui totali letti DENTRO la transazione: due richieste
 * contemporanee non possono superarlo. Se i totali non esistono ancora si partono da
 * quelli che chi scrive può vedere (totaliVisibili): completi per chi vede tutte le
 * richieste (che prima ha ricalcolato), solo le proprie per il PM — in quel caso restano
 * segnati «parziali» finché chi vede tutto non li ricalcola (all'apertura della pagina).
 */
export async function creaRichiestaFatturazione({ projectId, orgId, dati, totaliVisibili, completi }) {
  const budSnap = await budgetDellaCommessa(projectId, orgId);
  const refBudget = doc(db, "budgetCommessa", budSnap ? budSnap.id : idBudget(projectId));
  const refRichiesta = doc(collection(db, "richiesteFatturazione"));
  const tot = await runTransaction(db, async tx => {
    const b = await tx.get(refBudget);
    const salvati = b.exists() ? b.data().fatturazioneCalcolata : null;
    const prima = salvati || { ...riepilogoRichieste([]), ...(totaliVisibili || {}), parziale: !completi };
    if ((prima.percRichiesta || 0) + (parseFloat(dati.percentuale) || 0) > 100.001) {
      const e = new Error(`Sulla commessa è già stato richiesto il ${String(tondo(prima.percRichiesta)).replace(".", ",")}%: questa richiesta supererebbe il 100%.`);
      e.code = "nexiora/oltre-100";
      throw e;
    }
    tx.set(refRichiesta, dati);
    const nuovi = { ...applicaRichiesta(prima, dati, +1), parziale: !!prima.parziale };
    tx.set(refBudget, b.exists()
      ? { fatturazioneCalcolata: { ...nuovi, aggiornatiIl: serverTimestamp() } }
      : { projectId, organizationId: orgId, fatturazioneCalcolata: { ...nuovi, aggiornatiIl: serverTimestamp() } },
    { merge: true });
    return nuovi;
  });
  await riportaSulSAL(projectId, orgId, tot, false).catch(e => console.warn("SAL non aggiornato:", e));
  return refRichiesta.id;
}

/** Il PM annulla una sua richiesta ancora «Da fatturare»; i totali scendono nella stessa transazione. */
export async function annullaRichiestaFatturazione(richiestaId, chi) {
  const refR = doc(db, "richiesteFatturazione", richiestaId);
  const r0 = await getDoc(refR);
  if (!r0.exists()) throw new Error("La richiesta non esiste più");
  const { projectId, organizationId: orgId } = r0.data();
  const budSnap = await budgetDellaCommessa(projectId, orgId);
  const refBudget = budSnap ? doc(db, "budgetCommessa", budSnap.id) : null;
  const tot = await runTransaction(db, async tx => {
    const r = await tx.get(refR);
    const b = refBudget ? await tx.get(refBudget) : null;
    if (!r.exists() || r.data().stato !== "da_fatturare") {
      throw new Error("La richiesta è già stata presa in carico dall'amministrazione: non si può più annullare da qui");
    }
    tx.update(refR, { stato: "annullata", annullataDaNome: chi || "", annullataIl: serverTimestamp() });
    const prima = b?.exists() ? b.data().fatturazioneCalcolata : null;
    if (!prima) return null;
    const nuovi = { ...applicaRichiesta(prima, r.data(), -1), parziale: !!prima.parziale };
    tx.set(refBudget, { fatturazioneCalcolata: { ...nuovi, aggiornatiIl: serverTimestamp() } }, { merge: true });
    return nuovi;
  });
  if (tot) await riportaSulSAL(projectId, orgId, tot, false).catch(e => console.warn("SAL non aggiornato:", e));
}


/**
 * Percentuale del contratto già chiesta in fattura su una commessa. Chi vede tutte le
 * richieste la calcola da quelle; il PM usa i totali salvati sul budget (le richieste di
 * tutti), oppure — se non ci sono ancora — le proprie.
 */
export function percentualeRichiestaCommessa(projectId, richiesteVisibili, budget, vedeTutte) {
  const mie = riepilogoRichieste((richiesteVisibili || []).filter(r => r.projectId === projectId)).percRichiesta;
  if (vedeTutte) return mie;
  const salvata = budget?.fatturazioneCalcolata?.percRichiesta;
  return typeof salvata === "number" ? Math.max(salvata, mie) : mie;
}
